import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useTenant } from '../providers/TenantProvider';
import type { Booking, BookingStatus } from '../lib/db/types';

function toDatetimeLocalValue(ts?: Timestamp) {
  if (!ts) return '';
  const d = ts.toDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  // local time, without seconds
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): Timestamp {
  // value like 2026-02-07T10:30 (local)
  const d = new Date(value);
  return Timestamp.fromDate(d);
}

function statusColor(status: BookingStatus): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'scheduled':
      return 'warning';
    case 'completed':
      return 'success';
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
}

export function BookingsPage() {
  const { tenantId, role } = useTenant();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [bookings, setBookings] = useState<(Booking & { id: string })[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<(Booking & { id: string }) | null>(null);

  const [form, setForm] = useState({
    title: '',
    memberId: '',
    locationId: '',
    startAt: '',
    endAt: '',
    notes: '',
    status: 'scheduled' as BookingStatus,
  });

  const canWrite = role === 'owner' || role === 'manager' || role === 'staff';
  const canDelete = role === 'owner' || role === 'manager';

  useEffect(() => {
    if (!tenantId) return;
    void loadBookings();
  }, [tenantId]);

  const sortedBookings = useMemo(() => {
    return [...bookings].sort((a, b) => (b.startAt?.toMillis?.() || 0) - (a.startAt?.toMillis?.() || 0));
  }, [bookings]);

  async function loadBookings() {
    try {
      setLoading(true);
      const q = query(collection(db, 'bookings'), where('tenantId', '==', tenantId));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as (Booking & { id: string })[];
      setBookings(rows);
    } catch (err: any) {
      console.error('Error loading bookings:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to load bookings' });
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm({
      title: '',
      memberId: '',
      locationId: '',
      startAt: '',
      endAt: '',
      notes: '',
      status: 'scheduled',
    });
    setDialogOpen(true);
  }

  function openEdit(row: Booking & { id: string }) {
    setEditing(row);
    setForm({
      title: row.title,
      memberId: row.memberId || '',
      locationId: row.locationId || '',
      startAt: toDatetimeLocalValue(row.startAt),
      endAt: toDatetimeLocalValue(row.endAt),
      notes: row.notes || '',
      status: row.status,
    });
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditing(null);
  }

  async function saveBooking() {
    if (!tenantId) return;

    try {
      setMessage(null);

      if (!form.title.trim()) {
        setMessage({ type: 'error', text: 'Title is required' });
        return;
      }
      if (!form.startAt) {
        setMessage({ type: 'error', text: 'Start time is required' });
        return;
      }

      const startAt = fromDatetimeLocalValue(form.startAt);
      const endAt = form.endAt ? fromDatetimeLocalValue(form.endAt) : undefined;

      const memberId = form.memberId.trim();
      const locationId = form.locationId.trim();
      const notes = form.notes.trim();

      const baseData = {
        tenantId,
        title: form.title.trim(),
        startAt,
        status: form.status,
        updatedAt: Timestamp.now(),
      };

      if (editing) {
        await updateDoc(doc(db, 'bookings', editing.id), {
          ...baseData,
          endAt: endAt || deleteField(),
          memberId: memberId || deleteField(),
          locationId: locationId || deleteField(),
          notes: notes || deleteField(),
        } as any);
        setMessage({ type: 'success', text: 'Booking updated' });
      } else {
        await addDoc(collection(db, 'bookings'), {
          ...baseData,
          ...(endAt ? { endAt } : {}),
          ...(memberId ? { memberId } : {}),
          ...(locationId ? { locationId } : {}),
          ...(notes ? { notes } : {}),
          createdAt: Timestamp.now(),
        } as any);
        setMessage({ type: 'success', text: 'Booking created' });
      }

      closeDialog();
      await loadBookings();
    } catch (err: any) {
      console.error('Error saving booking:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to save booking' });
    }
  }

  async function removeBooking(id: string) {
    if (!confirm('Delete this booking?')) return;

    try {
      setMessage(null);
      await deleteDoc(doc(db, 'bookings', id));
      setMessage({ type: 'success', text: 'Booking deleted' });
      await loadBookings();
    } catch (err: any) {
      console.error('Error deleting booking:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to delete booking' });
    }
  }

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={3}>
        <Typography variant="h4" component="h1">Bookings</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            component={RouterLink}
            to="/appointments"
            variant="outlined"
            startIcon={<EventAvailableIcon />}
          >
            Appointments
          </Button>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Create Booking
            </Button>
          )}
        </Stack>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {sortedBookings.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" align="center">
              No bookings yet.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {sortedBookings.map((b) => (
            <Card key={b.id}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" noWrap>{b.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {b.startAt ? b.startAt.toDate().toLocaleString() : '—'}
                      {b.endAt ? ` → ${b.endAt.toDate().toLocaleString()}` : ''}
                    </Typography>
                    {b.notes && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {b.notes}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {b.memberId && <Chip size="small" label={`Member: ${b.memberId}`} />}
                      {b.locationId && <Chip size="small" label={`Location: ${b.locationId}`} />}
                      <Chip size="small" color={statusColor(b.status)} label={b.status.toUpperCase()} />
                    </Box>
                  </Box>

                  {canWrite && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small" onClick={() => openEdit(b)} aria-label="Edit booking">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      {canDelete && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeBooking(b.id)}
                          aria-label="Delete booking"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Booking' : 'Create Booking'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Start"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.startAt}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, startAt: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="End"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.endAt}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, endAt: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Status (scheduled / completed / canceled)"
              value={form.status}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, status: e.target.value as BookingStatus }))}
              fullWidth
              helperText="Keep to: scheduled, completed, canceled"
            />
            <TextField
              label="Member ID (optional)"
              value={form.memberId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, memberId: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Location ID (optional)"
              value={form.locationId}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, locationId: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={saveBooking} variant="contained" disabled={!form.title.trim() || !form.startAt}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
